import {
  BoxGeometry,
  BufferGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  CylinderGeometry,
  Group,
  InstancedMesh,
  LatheGeometry,
  Matrix4,
  SphereGeometry,
  TubeGeometry,
  Vector2,
  Vector3,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { STREET_LIGHT_RENDER_CONFIG } from "../../config/streetLights.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import type {
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import {
  isFullWidthPedestrianStreetFeature,
  streetFeatureRoadEdgeWidthM,
  streetFeatureSurfaceElevationM,
  streetSurfaceConfigForFeature,
  streetSurfaceFamilyForTile,
  terrainWithAuthoredStreetEarthworks,
} from "../../world/streets.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import { pointIntersectsRuntimeWaterSurface } from "../../world/water.ts";

export interface StreetLightBatch {
  readonly object: Group;
  readonly lightCount: number;
}

export interface StreetLightPlacement {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly sourceFeatureId: string;
  readonly archetype:
    | "historic-twin"
    | "modern-arterial"
    | "neighbourhood-single";
}

const ARCHETYPE_HEIGHT_M = 7.2;

function translated(
  geometry: BufferGeometry,
  x: number,
  y: number,
  z: number,
): BufferGeometry {
  geometry.translate(x, y, z);
  return geometry;
}

function cylinder(
  radiusTop: number,
  radiusBottom: number,
  height: number,
  y: number,
  radialSegments = 8,
): BufferGeometry {
  return translated(
    new CylinderGeometry(
      radiusTop,
      radiusBottom,
      height,
      radialSegments,
    ),
    0,
    y,
    0,
  );
}

function box(
  width: number,
  height: number,
  depth: number,
  x: number,
  y: number,
): BufferGeometry {
  return translated(new BoxGeometry(width, height, depth), x, y, 0);
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
  if (merged === null) {
    throw new Error(`Could not merge ${name}`);
  }
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function createArmGeometry(side: -1 | 1): BufferGeometry {
  const curve = new CatmullRomCurve3([
    new Vector3(side * 0.18, 6.48, 0),
    new Vector3(side * 0.34, 6.82, 0),
    new Vector3(side * 0.68, 6.98, 0),
    new Vector3(side * 0.96, 6.78, 0),
    new Vector3(side * 1.04, 6.34, 0),
  ]);
  return new TubeGeometry(curve, 12, 0.055, 6, false);
}

function createShadeGeometry(x: number): BufferGeometry {
  const geometry = new LatheGeometry([
    new Vector2(0.07, 0.1),
    new Vector2(0.11, 0.06),
    new Vector2(0.15, 0.01),
    new Vector2(0.24, -0.03),
    new Vector2(0.43, -0.12),
    new Vector2(0.51, -0.18),
    new Vector2(0.48, -0.22),
    new Vector2(0.18, -0.23),
  ], 12);
  geometry.translate(x, 6.17, 0);
  return geometry;
}

function createMetalArchetypeGeometry(): BufferGeometry {
  const geometries: BufferGeometry[] = [
    // Stepped cast base from the heavier reference post.
    cylinder(0.36, 0.39, 0.14, 0.07, 10),
    cylinder(0.29, 0.35, 0.13, 0.205, 10),
    cylinder(0.23, 0.29, 0.25, 0.395, 10),
    cylinder(0.18, 0.22, 0.52, 0.78, 10),
    cylinder(0.24, 0.24, 0.1, 1.09, 10),
    cylinder(0.14, 0.19, 0.17, 1.225, 10),
    // Slender tapered shaft and central finial.
    cylinder(0.075, 0.13, 5.22, 3.9, 8),
    cylinder(0.06, 0.075, 1.02, 6.55, 8),
    translated(new SphereGeometry(0.09, 8, 4), 0, 7.14, 0),
    // Twin gooseneck arms and the characteristic ladder bracket.
    createArmGeometry(-1),
    createArmGeometry(1),
    box(0.055, 0.72, 0.065, -0.2, 6.28),
    box(0.055, 0.72, 0.065, 0.2, 6.28),
    box(0.45, 0.055, 0.07, 0, 6.0),
    box(0.45, 0.055, 0.07, 0, 6.28),
    box(0.45, 0.055, 0.07, 0, 6.56),
  ];
  for (const side of [-1, 1] as const) {
    const x = side * 1.04;
    geometries.push(
      translated(new CylinderGeometry(0.11, 0.13, 0.2, 8), x, 6.22, 0),
      createShadeGeometry(x),
    );
  }
  return mergeOwned(geometries, "vilnius-twin-lamp:metal");
}

function createBulbArchetypeGeometry(): BufferGeometry {
  const globes = ([-1, 1] as const).map((side) => {
    const geometry = new SphereGeometry(0.23, 8, 5);
    geometry.scale(1, 0.78, 1);
    geometry.translate(side * 1.04, 5.91, 0);
    return geometry;
  });
  return mergeOwned(globes, "vilnius-twin-lamp:glass");
}

function createModernArterialMetalGeometry(): BufferGeometry {
  return mergeOwned([
    cylinder(0.27, 0.32, 0.18, 0.09, 10),
    cylinder(0.11, 0.19, 7.65, 3.98, 10),
    translated(new BoxGeometry(1.45, 0.1, 0.1), 0.65, 7.78, 0),
    translated(new BoxGeometry(0.72, 0.12, 0.32), 1.38, 7.69, 0),
  ], "vilnius-modern-arterial-lamp:metal");
}

function createModernArterialBulbGeometry(): BufferGeometry {
  return translated(new BoxGeometry(0.58, 0.045, 0.23), 1.38, 7.62, 0);
}

function createNeighbourhoodMetalGeometry(): BufferGeometry {
  return mergeOwned([
    cylinder(0.22, 0.27, 0.16, 0.08, 10),
    cylinder(0.09, 0.15, 5.22, 2.69, 9),
    translated(new CylinderGeometry(0.14, 0.18, 0.14, 10), 0, 5.35, 0),
    translated(new CylinderGeometry(0.34, 0.16, 0.2, 10), 0, 5.52, 0),
  ], "vilnius-neighbourhood-lamp:metal");
}

function createNeighbourhoodBulbGeometry(): BufferGeometry {
  const bulb = new SphereGeometry(0.22, 9, 6);
  bulb.scale(1, 0.82, 1);
  bulb.translate(0, 5.31, 0);
  return bulb;
}

export function createStreetLightBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  groundingTerrain?: Readonly<TerrainTileData>,
): StreetLightBatch | null {
  const positions = streetLightPlacementsForTile(tile, groundingTerrain);
  if (positions.length === 0) {
    return null;
  }
  const object = new Group();
  object.name = `runtime-street-lights:${tile.id}`;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "road-centerlines",
    placement: "deterministic-official-road-edge-sidewalk-inference",
    representation:
      "district-aware-historic-twin-modern-arterial-and-neighbourhood-low-poly",
  };
  const matrix = new Matrix4();
  for (const archetype of [
    "historic-twin",
    "modern-arterial",
    "neighbourhood-single",
  ] as const) {
    const archetypePositions = positions.filter((position) =>
      position.archetype === archetype
    );
    if (archetypePositions.length === 0) continue;
    const heightM = archetype === "historic-twin"
      ? STREET_LIGHT_RENDER_CONFIG.heightM
      : archetype === "modern-arterial"
      ? STREET_LIGHT_RENDER_CONFIG.modernArterialHeightM
      : STREET_LIGHT_RENDER_CONFIG.neighbourhoodHeightM;
    const sourceHeightM = archetype === "historic-twin"
      ? ARCHETYPE_HEIGHT_M
      : archetype === "modern-arterial"
      ? 8
      : 5.8;
    const metalGeometry = archetype === "historic-twin"
      ? createMetalArchetypeGeometry()
      : archetype === "modern-arterial"
      ? createModernArterialMetalGeometry()
      : createNeighbourhoodMetalGeometry();
    const bulbGeometry = archetype === "historic-twin"
      ? createBulbArchetypeGeometry()
      : archetype === "modern-arterial"
      ? createModernArterialBulbGeometry()
      : createNeighbourhoodBulbGeometry();
    const scale = heightM / sourceHeightM;
    metalGeometry.scale(scale, scale, scale);
    bulbGeometry.scale(scale, scale, scale);
    const poles = new InstancedMesh(
      metalGeometry,
      materials.streetPole,
      archetypePositions.length,
    );
    const bulbs = new InstancedMesh(
      bulbGeometry,
      materials.lampBulb,
      archetypePositions.length,
    );
    const pools = new InstancedMesh(
      new CircleGeometry(STREET_LIGHT_RENDER_CONFIG.lightPoolRadiusM, 24),
      materials.lampPool,
      archetypePositions.length,
    );
    poles.name = archetype === "historic-twin"
      ? `${object.name}:vilnius-twin-lamp-metal`
      : `${object.name}:${archetype}:metal`;
    bulbs.name = archetype === "historic-twin"
      ? `${object.name}:faceted-globes`
      : `${object.name}:${archetype}:bulbs`;
    pools.name = `${object.name}:${archetype}:warm-ground-pools`;
    poles.castShadow = false;
    bulbs.castShadow = false;
    pools.castShadow = false;
    pools.receiveShadow = false;
    pools.renderOrder = 3;
    for (const [index, position] of archetypePositions.entries()) {
      matrix.makeRotationY(position.yaw);
      matrix.setPosition(position.x, position.y, position.z);
      poles.setMatrixAt(index, matrix);
      bulbs.setMatrixAt(index, matrix);
      matrix.makeRotationX(-Math.PI / 2);
      matrix.setPosition(
        position.x,
        position.y + STREET_LIGHT_RENDER_CONFIG.lightPoolLiftM,
        position.z,
      );
      pools.setMatrixAt(index, matrix);
    }
    poles.instanceMatrix.needsUpdate = true;
    bulbs.instanceMatrix.needsUpdate = true;
    pools.instanceMatrix.needsUpdate = true;
    object.add(poles, bulbs, pools);
  }
  return { object, lightCount: positions.length };
}

export function streetLightPlacementsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  groundingTerrain?: Readonly<TerrainTileData>,
): readonly StreetLightPlacement[] {
  const streetFamily = streetSurfaceFamilyForTile(tile.origin, tile.sizeM);
  const terrain = groundingTerrain ?? terrainWithAuthoredStreetEarthworks(
    tile.terrain,
    tile.origin,
  );
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  return roads.flatMap((feature) => {
    const surface = streetSurfaceConfigForFeature(feature, streetFamily);
    const sidewalkInsetM = Math.min(
      STREET_LIGHT_RENDER_CONFIG.sidewalkInsetM,
      surface.sidewalkWidthM / 2,
    );
    const roadsideOffsetM = isFullWidthPedestrianStreetFeature(feature)
      ? Math.max(0, streetFeatureRoadEdgeWidthM(feature) / 2 - sidewalkInsetM)
      : streetFeatureRoadEdgeWidthM(feature) / 2 +
        surface.curbWidthM + sidewalkInsetM;
    return positionsAlongFeature(
      feature,
      tile,
      terrain,
      roadsideOffsetM,
      streetLightArchetype(feature, streetFamily),
    )
      .filter((placement) =>
        !tile.buildings.some((building) =>
          pointInPolygon(placement, building.footprint)
        ) &&
        // Authored bridge assets own their deck furniture. A generic lamp
        // grounded to the terrain here would be mostly submerged in the river.
        !tile.waterLines.some((water) =>
          pointIntersectsRuntimeWaterSurface(
            placement,
            water,
            tile.transportLines,
          )
        ) &&
        !roads.some((road) => {
          if (
            (road.id === feature.id &&
              isFullWidthPedestrianStreetFeature(feature)) ||
            distanceToPolyline(placement, road.points) >=
              streetFeatureRoadEdgeWidthM(road) / 2 + 0.15
          ) {
            return false;
          }
          const crossingElevationM = streetFeatureSurfaceElevationM(
            road,
            terrain,
            tile.origin,
            placement,
          );
          return crossingElevationM !== null &&
            Math.abs(crossingElevationM - placement.y) < 1;
        })
      );
  });
}

function positionsAlongFeature(
  feature: Readonly<RuntimeLinearFeature>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  roadsideOffsetM: number,
  archetype: StreetLightPlacement["archetype"],
): readonly StreetLightPlacement[] {
  const positions: StreetLightPlacement[] = [];
  let travelledM = 0;
  let nextDistanceM = STREET_LIGHT_RENDER_CONFIG.spacingM / 2;
  for (let index = 1; index < feature.points.length; index += 1) {
    const from = feature.points[index - 1];
    const to = feature.points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 0.01) {
      continue;
    }
    while (nextDistanceM <= travelledM + lengthM) {
      const t = (nextDistanceM - travelledM) / lengthM;
      const side = positions.length % 2 === 0 ? 1 : -1;
      const x =
        from.x + deltaX * t -
        deltaZ / lengthM * roadsideOffsetM * side;
      const z =
        from.z + deltaZ * t +
        deltaX / lengthM * roadsideOffsetM * side;
      const y = streetFeatureSurfaceElevationM(
        feature,
        terrain,
        tile.origin,
        { x, z },
      );
      if (y !== null) {
        positions.push({
          x,
          y,
          z,
          yaw: Math.atan2(-deltaZ, deltaX),
          sourceFeatureId: feature.id,
          archetype,
        });
      }
      nextDistanceM += STREET_LIGHT_RENDER_CONFIG.spacingM;
    }
    travelledM += lengthM;
  }
  return positions;
}

function streetLightArchetype(
  feature: Readonly<RuntimeLinearFeature>,
  streetFamily: ReturnType<typeof streetSurfaceFamilyForTile>,
): StreetLightPlacement["archetype"] {
  if (
    streetFamily === "old_town" ||
    isFullWidthPedestrianStreetFeature(feature)
  ) {
    return "historic-twin";
  }
  if (
    streetFeatureRoadEdgeWidthM(feature) >= 12 ||
    (feature.maxspeedKph ?? 0) >= 60
  ) {
    return "modern-arterial";
  }
  return "neighbourhood-single";
}

function distanceToPolyline(
  point: Readonly<{ x: number; z: number }>,
  line: readonly Readonly<{ x: number; z: number }>[],
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1];
    const to = line[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const amount = lengthSquared === 0
      ? 0
      : Math.max(0, Math.min(1,
          ((point.x - from.x) * deltaX + (point.z - from.z) * deltaZ) /
            lengthSquared,
        ));
    minimum = Math.min(minimum, Math.hypot(
      point.x - (from.x + deltaX * amount),
      point.z - (from.z + deltaZ * amount),
    ));
  }
  return minimum;
}
